import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  CircularProgress,
  Alert,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Avatar,
  Button,
  FormControl,
  Select,
  MenuItem,
  Chip,
  Box,
  Paper,
  Card,
  Stack,
  useTheme,
  alpha,
  Tooltip,
  Badge,
} from "@mui/material";
import {
  Comment as CommentIcon,
  ArrowForward as ArrowForwardIcon,
  Create as CreateIcon,
  AttachFile as AttachFileIcon,
  Send as SendIcon,
  Inbox as InboxIcon,
  FormatBold as FormatBoldIcon,
  FormatItalic as FormatItalicIcon,
  FormatUnderlined as FormatUnderlinedIcon,
  StrikethroughS as StrikethroughSIcon,
  FormatListBulleted as FormatListBulletedIcon,
  FormatListNumbered as FormatListNumberedIcon,
  FormatQuote as FormatQuoteIcon,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import api from "../services/api";

const JTInbox = () => {
  // Unread messages state
  const [unreadMessages, setUnreadMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Compose form states
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeComment, setComposeComment] = useState("");
  const [selectedProject, setSelectedProject] = useState("");
  const [projects, setProjects] = useState([]);
  const [cascadingNames, setCascadingNames] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [subject, setSubject] = useState('');
  const [ccInput, setCcInput] = useState('');
  const [ccList, setCcList] = useState([]);
  const editorRef = useRef(null);

  const navigate = useNavigate();
  const email = localStorage.getItem("username");

  // Show desktop notification for new messages
  const showNotification = (message) => {
    if (Notification.permission === "granted") {
      new Notification("New Message", {
        body: `${message.poster_name}: ${message.comment}`,
        icon: "/notification_icon.png", // Replace with a valid icon URL
      });
    }
  };

  // Fetch unread messages every 5 seconds
  useEffect(() => {
    let intervalId;
    if (Notification.permission !== "granted") {
      Notification.requestPermission().then((permission) => {
        console.log("Notification permission:", permission);
      });
    }

   const fetchUnreadMessages = async () => {
      try {
        if (!email) throw new Error("Username not found in local storage");
        
        const response = await api.get(
          `discussion/unread-messages/`
        );
        
        const newMessages = response.data.unread_messages || [];
        if (JSON.stringify(newMessages) !== JSON.stringify(unreadMessages)) {
            if (newMessages.length > 0 && newMessages.length > unreadMessages.length) {
                showNotification(newMessages[0]);
            }
            setUnreadMessages(newMessages);
        }
      } catch (error) {
        console.error("Error fetching unread messages:", error);
        setError("An error occurred while fetching unread messages.");
      } finally {
        setLoading(false);
      }
    };

    fetchUnreadMessages();
    intervalId = setInterval(fetchUnreadMessages, 500000);
    return () => clearInterval(intervalId);
  }, [unreadMessages, email]);

  // Fetch cascading names (from user profile)
  useEffect(() => {
    const token = localStorage.getItem("Token");
    if (!email || !token) return;
    api
      .get(`users/cascading_names/?email=${email}`, {
        headers: { Authorization: `Token ${token}` },
      })
      .then((response) => {
        setCascadingNames(response.data);
      })
      .catch((error) => {
        console.error("Error fetching cascading names:", error);
      });
  }, [email]);

  // Fetch projects using cascading names
  useEffect(() => {
    const token = localStorage.getItem("Token");
    if (!token) return;
    if (cascadingNames.length === 0) return;
    const cascadingNamesParam = cascadingNames.join("&name=");
    api
      .get(`api/segments/fetch-segments-by-name?name=${cascadingNamesParam}`, {
        headers: { Authorization: `Token ${token}` },
      })
      .then((response) => {
        setProjects(response.data);
      })
      .catch((error) =>
        console.error("Error fetching projects:", error)
      );
  }, [cascadingNames]);

  // Handle clicking on an unread message row to navigate to discussion
  const handleListItemClick = (segmentId) => {
    navigate("/discussion", { state: { segmentId } });
  };
  
  const handleFileChange = (e) => {
    setAttachments([...e.target.files]);
  };

  const execFormat = (cmd, value = null) => {
    document.execCommand(cmd, false, value);
    editorRef.current?.focus();
  };

  const handleCcKeyDown = (e) => {
    if ((e.key === 'Enter' || e.key === ',') && ccInput.trim()) {
      e.preventDefault();
      const addr = ccInput.trim().replace(/,$/, '');
      if (addr && !ccList.includes(addr)) setCcList([...ccList, addr]);
      setCcInput('');
    }
    if (e.key === 'Backspace' && !ccInput && ccList.length > 0) {
      setCcList(ccList.slice(0, -1));
    }
  };

  const handleComposeSubmit = async (e) => {
    e.preventDefault();
    const messageHtml = editorRef.current?.innerHTML || '';
    const messagePlain = editorRef.current?.innerText?.trim() || '';
    if (!selectedProject || !messagePlain) {
      setError("Please select a project and enter a message.");
      return;
    }
    try {
      setLoading(true);
      setError("");

      const projectObj = JSON.parse(selectedProject);
      const { job_number, segment_name } = projectObj;

      const segResponse = await api.get(
        `api/segments/fetch_segment_id_by_job_and_name?job_number=${job_number}&segment_name=${segment_name}`
      );
      const { segmentId } = segResponse.data;
      if (!segmentId) {
        setError("Segment ID not found for the selected project.");
        setLoading(false);
        return;
      }

      const formData = new FormData();
      formData.append('segment', segmentId);
      formData.append('userId', email);
      formData.append('comment', messageHtml);
      formData.append('send_email', 'true');
      if (subject.trim()) formData.append('custom_subject', subject.trim());
      if (ccList.length > 0) formData.append('cc', ccList.join(','));
      attachments.forEach(file => formData.append('attachments', file));

      await api.post(`discussion/create/`, formData, {
        headers: { Authorization: `Token ${localStorage.getItem("Token")}` },
      });

      setSelectedProject('');
      setAttachments([]);
      setSubject('');
      setCcList([]);
      setCcInput('');
      if (editorRef.current) editorRef.current.innerHTML = '';
      setComposeOpen(false);
    } catch (err) {
      console.error("Error posting comment:", err);
      setError(err.response?.data?.error || "Failed to send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const theme = useTheme();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background:
          theme.palette.mode === "light"
            ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.03)} 0%, ${alpha(theme.palette.secondary.main, 0.03)} 100%)`
            : `linear-gradient(135deg, ${alpha(theme.palette.background.default, 0.95)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
        py: 2,
        px: { xs: 1, sm: 1.5, md: 2 },
      }}
    >
      <Box sx={{ maxWidth: '100%', mx: 0 }}>
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Box
            sx={{
              mb: 3,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 2,
              p: 2,
              borderRadius: 3,
              border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
              bgcolor: alpha(theme.palette.background.paper, 0.6),
              backdropFilter: 'blur(10px)',
              boxShadow: `0 2px 12px ${alpha(theme.palette.common.black, 0.03)}`,
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar
                sx={{
                  width: 48,
                  height: 48,
                  bgcolor: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                }}
              >
                <InboxIcon />
              </Avatar>
              <Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 800,
                    letterSpacing: -0.5,
                    mb: 0.5,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontSize: { xs: '1.5rem', md: '2rem' },
                  }}
                >
                  Unread Messages
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem' }}>
                  Stay updated with project discussions • {unreadMessages.length} unread message{unreadMessages.length !== 1 ? 's' : ''}
                </Typography>
              </Box>
            </Stack>
            <Button
              variant="contained"
              startIcon={<CreateIcon />}
              onClick={() => setComposeOpen((prev) => !prev)}
              sx={{
                borderRadius: 2.5,
                px: 3,
                py: 1.2,
                textTransform: 'none',
                fontSize: '0.95rem',
                fontWeight: 600,
                boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.4)}`,
                background: composeOpen
                  ? theme.palette.grey[500]
                  : `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                '&:hover': {
                  boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.5)}`,
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              {composeOpen ? "Cancel" : "Compose"}
            </Button>
          </Box>
        </motion.div>
        {/* Compose Form — Outlook style */}
        {composeOpen && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
            <style>{`
              .compose-editor:empty:before {
                content: attr(data-placeholder);
                color: #9ca3af;
                pointer-events: none;
                display: block;
              }
              .compose-editor blockquote {
                border-left: 3px solid #6366f1;
                margin: 4px 0;
                padding-left: 12px;
                color: #6b7280;
              }
              .compose-editor ul { padding-left: 20px; margin: 4px 0; }
              .compose-editor ol { padding-left: 20px; margin: 4px 0; }
            `}</style>
            <Card elevation={0} sx={{
              mb: 2.5, borderRadius: 3,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              background: theme.palette.background.paper,
              boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.1)}`,
              overflow: 'hidden',
            }}>
              {/* Title bar */}
              <Box sx={{
                px: 2.5, py: 1.5,
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.secondary.main, 0.06)})`,
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                display: 'flex', alignItems: 'center', gap: 1,
              }}>
                <CreateIcon sx={{ color: 'primary.main', fontSize: 18 }} />
                <Typography sx={{ fontWeight: 700, fontSize: 14, flex: 1, color: 'primary.main' }}>New Message</Typography>
              </Box>

              {error && <Alert severity="error" sx={{ mx: 2.5, mt: 1.5, borderRadius: 2 }}>{error}</Alert>}

              <form onSubmit={handleComposeSubmit}>
                {/* To: */}
                <Box sx={{
                  display: 'flex', alignItems: 'center', px: 2.5, py: 1,
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.35)}`,
                }}>
                  <Typography sx={{ minWidth: 64, fontSize: 12.5, fontWeight: 700, color: 'text.secondary', letterSpacing: 0.3 }}>To</Typography>
                  <FormControl size="small" sx={{ flex: 1 }}>
                    <Select
                      value={selectedProject}
                      displayEmpty
                      onChange={(e) => {
                        setSelectedProject(e.target.value);
                        if (e.target.value) {
                          const proj = JSON.parse(e.target.value);
                          const name = proj.project_name || proj.job_number || '';
                          setSubject(`[${name}] ${proj.segment_name} | Discussion`);
                        }
                      }}
                      sx={{
                        '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                        '& .MuiSelect-select': { fontSize: 13, py: 0.4, pl: 0 },
                        bgcolor: 'transparent',
                      }}
                    >
                      <MenuItem value="" disabled><em style={{ color: '#9ca3af' }}>Select project…</em></MenuItem>
                      {projects.map((proj, i) => (
                        <MenuItem key={i} value={JSON.stringify(proj)}>
                          {proj.project_name} — {proj.segment_name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                {/* CC: */}
                <Box sx={{
                  display: 'flex', alignItems: 'center', px: 2.5, py: 0.75, flexWrap: 'wrap', gap: 0.5,
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.35)}`,
                }}>
                  <Typography sx={{ minWidth: 64, fontSize: 12.5, fontWeight: 700, color: 'text.secondary', letterSpacing: 0.3 }}>CC</Typography>
                  <Box sx={{ display: 'flex', flex: 1, flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                    {ccList.map((addr, i) => (
                      <Chip
                        key={i} label={addr} size="small"
                        onDelete={() => setCcList(ccList.filter((_, j) => j !== i))}
                        sx={{ fontSize: 12, height: 22, bgcolor: alpha(theme.palette.primary.main, 0.08) }}
                      />
                    ))}
                    <input
                      value={ccInput}
                      onChange={e => setCcInput(e.target.value)}
                      onKeyDown={handleCcKeyDown}
                      placeholder={ccList.length === 0 ? 'Add email, press Enter' : ''}
                      style={{
                        border: 'none', outline: 'none', fontSize: 13,
                        background: 'transparent', flex: 1, minWidth: 160,
                        padding: '2px 0', color: 'inherit',
                      }}
                    />
                  </Box>
                </Box>

                {/* Subject: */}
                <Box sx={{
                  display: 'flex', alignItems: 'center', px: 2.5, py: 1,
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.35)}`,
                }}>
                  <Typography sx={{ minWidth: 64, fontSize: 12.5, fontWeight: 700, color: 'text.secondary', letterSpacing: 0.3 }}>Subject</Typography>
                  <input
                    value={subject}
                    onChange={e => setSubject(e.target.value)}
                    placeholder="Subject"
                    style={{
                      border: 'none', outline: 'none', fontSize: 13,
                      flex: 1, background: 'transparent', color: 'inherit',
                      fontWeight: 500, padding: '4px 0',
                    }}
                  />
                </Box>

                {/* Formatting toolbar */}
                <Box sx={{
                  display: 'flex', gap: 0.25, px: 1, py: 0.5, alignItems: 'center',
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.35)}`,
                  bgcolor: alpha(theme.palette.background.default, 0.6),
                  flexWrap: 'wrap',
                }}>
                  {[
                    { icon: <FormatBoldIcon sx={{ fontSize: 17 }} />, cmd: 'bold', title: 'Bold' },
                    { icon: <FormatItalicIcon sx={{ fontSize: 17 }} />, cmd: 'italic', title: 'Italic' },
                    { icon: <FormatUnderlinedIcon sx={{ fontSize: 17 }} />, cmd: 'underline', title: 'Underline' },
                    { icon: <StrikethroughSIcon sx={{ fontSize: 17 }} />, cmd: 'strikeThrough', title: 'Strikethrough' },
                  ].map(({ icon, cmd, title }) => (
                    <Tooltip key={cmd} title={title}>
                      <IconButton
                        size="small"
                        onMouseDown={e => { e.preventDefault(); execFormat(cmd); }}
                        sx={{ borderRadius: 1, p: 0.6 }}
                      >
                        {icon}
                      </IconButton>
                    </Tooltip>
                  ))}
                  <Box sx={{ width: '1px', height: 18, bgcolor: 'divider', mx: 0.5 }} />
                  {[
                    { icon: <FormatListBulletedIcon sx={{ fontSize: 17 }} />, cmd: 'insertUnorderedList', title: 'Bullet list' },
                    { icon: <FormatListNumberedIcon sx={{ fontSize: 17 }} />, cmd: 'insertOrderedList', title: 'Numbered list' },
                  ].map(({ icon, cmd, title }) => (
                    <Tooltip key={cmd} title={title}>
                      <IconButton
                        size="small"
                        onMouseDown={e => { e.preventDefault(); execFormat(cmd); }}
                        sx={{ borderRadius: 1, p: 0.6 }}
                      >
                        {icon}
                      </IconButton>
                    </Tooltip>
                  ))}
                  <Box sx={{ width: '1px', height: 18, bgcolor: 'divider', mx: 0.5 }} />
                  <Tooltip title="Blockquote">
                    <IconButton
                      size="small"
                      onMouseDown={e => { e.preventDefault(); execFormat('formatBlock', 'blockquote'); }}
                      sx={{ borderRadius: 1, p: 0.6 }}
                    >
                      <FormatQuoteIcon sx={{ fontSize: 17 }} />
                    </IconButton>
                  </Tooltip>
                </Box>

                {/* Rich text editor body */}
                <Box sx={{ px: 2.5, pt: 1.5, pb: 1, minHeight: 160 }}>
                  <div
                    ref={editorRef}
                    contentEditable
                    className="compose-editor"
                    data-placeholder="Write your message here…"
                    suppressContentEditableWarning
                    style={{
                      minHeight: 140, outline: 'none', fontSize: 14,
                      lineHeight: 1.7, color: 'inherit', fontFamily: 'inherit',
                    }}
                  />
                </Box>

                {/* Footer: attach + send */}
                <Box sx={{
                  px: 2.5, py: 1.5,
                  borderTop: `1px solid ${alpha(theme.palette.divider, 0.35)}`,
                  display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap',
                  bgcolor: alpha(theme.palette.background.default, 0.5),
                }}>
                  <Button
                    variant="text" component="label" startIcon={<AttachFileIcon />} size="small"
                    sx={{ textTransform: 'none', color: 'text.secondary', borderRadius: 2, flexShrink: 0 }}
                  >
                    Attach Files
                    <input type="file" hidden multiple onChange={handleFileChange} />
                  </Button>
                  {attachments.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, flex: 1 }}>
                      {attachments.map((file, i) => (
                        <Chip
                          key={i} label={file.name} size="small"
                          onDelete={() => setAttachments(attachments.filter((_, j) => j !== i))}
                          sx={{ fontSize: 11 }}
                        />
                      ))}
                    </Box>
                  )}
                  <Box sx={{ ml: 'auto' }}>
                    <Button
                      type="submit"
                      variant="contained"
                      startIcon={loading ? null : <SendIcon />}
                      disabled={loading}
                      sx={{
                        borderRadius: 2, px: 3.5, textTransform: 'none', fontWeight: 700,
                        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                        '&:hover': { boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.4)}` },
                      }}
                    >
                      {loading ? <CircularProgress size={20} color="inherit" /> : 'Send'}
                    </Button>
                  </Box>
                </Box>
              </form>
            </Card>
          </motion.div>
        )}
        {/* Messages Table */}
        {loading && !composeOpen ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={40} />
            <Typography sx={{ mt: 2 }}>Loading unread messages...</Typography>
          </Box>
        ) : !error && unreadMessages.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
          >
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              No unread messages found.
            </Alert>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
          >
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
                background:
                  theme.palette.mode === "light"
                    ? alpha(theme.palette.background.paper, 0.8)
                    : alpha(theme.palette.background.paper, 0.9),
                backdropFilter: 'blur(10px)',
                boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.05)}`,
              }}
            >
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                      Avatar
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                      Segment Name
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                      Project Name
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                      Poster
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                      Message
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                      Timestamp
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {unreadMessages.map((message, index) => (
                    <TableRow
                      key={index}
                      hover
                      sx={{
                        cursor: 'pointer',
                        '&:hover': {
                          bgcolor: alpha(theme.palette.primary.main, 0.05),
                        },
                      }}
                      onClick={() => handleListItemClick(message.segment_id)}
                    >
                      <TableCell>
                        <Badge
                          badgeContent="New"
                          color="error"
                          sx={{
                            '& .MuiBadge-badge': {
                              fontSize: '0.6rem',
                              fontWeight: 700,
                            },
                          }}
                        >
                          <Avatar
                            sx={{
                              bgcolor: alpha(theme.palette.primary.main, 0.15),
                              color: 'primary.main',
                            }}
                          >
                            <CommentIcon />
                          </Avatar>
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {message.segment_name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {message.job_number ? `${message.job_number} — ` : ''}{message.project_name || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={message.poster_name}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            bgcolor: alpha(theme.palette.info.main, 0.1),
                            color: 'info.main',
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 300 }}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {message.comment.split("\n")[0]}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(message.timestamp).toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="View Discussion">
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              handleListItemClick(message.segment_id);
                            }}
                            sx={{
                              color: 'primary.main',
                              '&:hover': {
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                              },
                            }}
                          >
                            <ArrowForwardIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </motion.div>
        )}
      </Box>
    </Box>
  );
};

export default JTInbox;